<script setup>
import { defineProps, ref, defineEmits } from "vue";
import TransactionsView from "./TransactionsView.vue";
import VendorDetailDialog from "./VendorDetailDialog.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import ToggleButton from "primevue/togglebutton";
import { useToast } from "primevue/usetoast";
import { formatCurrency } from "@/util/format";
import { dashboardAPI } from "../service/api.js";

const expandedRows = ref({});
const transactionMap = ref({});
const regeneratingIds = ref({});
const showVendorDialog = ref(false);
const selectedVendorId = ref(null);
const filters = ref({
  global: { value: null, matchMode: "contains" },
  status: { value: null, matchMode: "contains" },
});

const toast = useToast();
const emit = defineEmits(['batchUpdated']);

const fetchTransactions = async (batchId) => {
  try {
    const response = await dashboardAPI.getTransactionsByBatchId(batchId);
    return response.data.data; // Adjust based on the API response structure
  } catch (error) {
    console.error("Error fetching transactions:", error);
  }
};

const regenerateIds = async (batch) => {
  try {
    regeneratingIds.value[batch.id] = true;
    const response = await dashboardAPI.regenerateTransactionBatchIds(batch.id);
    
    // Update the batch with new IDs
    batch.external_batch_id = response.data.external_batch_id;
    batch.carepay_id = response.data.carepay_id;
    
    // Emit event to parent to update the data
    emit('batchUpdated', batch);
  } catch (error) {
    console.error("Error regenerating IDs:", error);
  } finally {
    regeneratingIds.value[batch.id] = false;
  }
};

const onExpand = async (event) => {
  const batchId = event.data.id;

  const transactions = await fetchTransactions(batchId);
  if (transactions) {
    transactionMap.value[batchId] = transactions;
  }
};

const toggleStuckStatus = async (batch) => {
  try {
    const newStatus = batch.status === 'STUCK' ? 'UNSTUCK' : 'STUCK';
    await dashboardAPI.updateTransactionBatchStatus(batch.id, newStatus);

    // Update the local data
    batch.status = newStatus;

    // Emit event to parent to update the data
    emit('batchUpdated', batch);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: `Transaction batch marked as ${newStatus}`,
      life: 3000
    });
  } catch (error) {
    console.error('Error updating transaction batch status:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update transaction batch status',
      life: 3000
    });
  }
};

const openVendorDialog = (vendorId) => {
  if (vendorId) {
    selectedVendorId.value = vendorId;
    showVendorDialog.value = true;
  }
};

defineProps({
  transactionBatches: {
    type: Array,
    required: true,
  },
});
</script>

<template>
  <div>
    <DataTable
      class="tb-table"
      size="medium"
      :filters="filters"
      filterDisplay="row"
      :expandedRows="expandedRows"
      :value="transactionBatches"
      dataKey="id"
      @rowExpand="onExpand"
      :globalFilterFields="['status']"
      editMode="cell"
      :rowClass="(data) => data.status === 'STUCK' ? 'stuck-row' : ''"
    >
      <template #empty> No transactions found. </template>
      <Column expander style="width: 5rem" />
      <Column header="Regenerate Ids" style="width: 120px">
        <template #body="slotProps">
          <Button
            size="small"
            icon="pi pi-replay"
            :loading="regeneratingIds[slotProps.data.id]"
            @click="regenerateIds(slotProps.data)"
          />
        </template>
      </Column>
      <Column header="Stuck" style="width: 100px">
        <template #body="slotProps">
          <ToggleButton
            v-model="slotProps.data.stuck"
            :modelValue="slotProps.data.status === 'STUCK'"
            onLabel="STUCK"
            offLabel="OK"
            onIcon="pi pi-exclamation-triangle"
            offIcon="pi pi-check"
            size="small"
            severity="danger"
            @change="toggleStuckStatus(slotProps.data)"
          />
        </template>
      </Column>
      <Column field="external_batch_id" header="Payment ID">
        <template #body="slotProps">
          <a
            :href="`/#/c/${slotProps.data.external_batch_id}`"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ slotProps.data.external_batch_id }}
          </a>
        </template>
      </Column>
      <Column field="carepay_id" header="Carepay ID"></Column>
      <Column field="carepay_vendor_id" header="Carepay Vendor ID">
        <template #body="slotProps">
          <a
            v-if="slotProps.data.vendor_id"
            href="javascript:void(0)"
            class="vendor-link"
            @click="openVendorDialog(slotProps.data.vendor_id)"
          >
            {{ slotProps.data.carepay_vendor_id }}
          </a>
          <span v-else>N/A</span>
        </template>
      </Column>
      <Column sortable field="amount" header="Amount">
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.amount) }}
        </template>
      </Column>

      <template #expansion="slotProps">
        <div>
          <h5>
            Transaction Count
            {{
              transactionMap[slotProps.data.id]
                ? transactionMap[slotProps.data.id].length
                : 0
            }}
          </h5>
          <TransactionsView
            size="small"
            :transactions="transactionMap[slotProps.data.id]"
          />
        </div>
      </template>
    </DataTable>

    <VendorDetailDialog
      v-model:visible="showVendorDialog"
      :vendorId="selectedVendorId"
    />
  </div>
</template>

<style scoped>
.tb-table:deep(.p-datatable-thead > tr > th) {
  background-color: #e3f2fd;
}

.tb-table:deep(.p-datatable-tbody > tr) {
  background-color: #e3f2fd;
}

.tb-table:deep(.p-datatable-tbody > tr:hover) {
  background-color: #bbdefb;
}

.tb-table:deep(.stuck-row) {
  background-color: #ffebee !important;
  border-left: 4px solid #f44336;
}

.tb-table:deep(.stuck-row:hover) {
  background-color: #ffcdd2 !important;
}

.vendor-link {
  color: #1976d2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
}

.vendor-link:hover {
  color: #1565c0;
  text-decoration: underline;
}
</style>
